// IE 2026 10 26 / ODOO-5800 (слово Igor 26.10 через ie-dm7 #106105: граф схемы — наконечники, ⚑ прячет подсказки, высота, вид узлов): вид узлов графа схемы v2 (AttrSchemeGraphV2).
// Переключатель — в быстрых настройках ⚙ графа, значение — настройка вида (viewParams.<граф>.node_view, как layout_mode / graph_edge_arrows: без пометки записи, уходит с ближайшим Save).
// Бэк: params.node_view (умолчание вида, 'box') и params.node_views [{ id, title, title_uk, title_ru }] (список); пока бэк не прислал — box / card с подписями t().
//   'box'  — прямоугольник с названием, как было;
//   'card' — карточка, как на вкладке ERD: название, вид узла, пилюли горизонтов, поля с ролью ◇ / Σ / i — по тем же привязкам, что у ERD (GRAPH_CARD_BINDINGS — умолчания
//            таблиц записи DATA-SCHEME, если атрибут графа их не задал); сборка и подписи — utils/schemeGraphErd.js (sgeCardOf / sgeCardW / sgeCardText), рисует graph/SchemeErdCard.vue;
//            раскладка и маршруты связей графа — те же, по размерам карточек;
//   'cascade' — РЕЗЕРВ (каскад; не сделан): бэк может прислать — в списке не показывается, вид — box (SG2_NODE_VIEW_RESERVED).
// Выключатель fe.ux.schemeGraphNodeView (§8, дефолт ВКЛ; выкл — всегда box, пункта в ⚙ нет). Тест: tests/unit/schemeGraphNodeView.test.mjs
import { t } from '@/i18n'
import { localized } from '@/utils/schemeGraph'
import { sg2Hex } from '@/utils/schemeGraphV2'
import { SGE, SGE_LANE_COLORS, SGE_PLAIN, sgeConfig, sgeFieldsMap, sgeHorizonsMap, sgeNodeFields, sgeNodeHorizons, sgeRowsAt, sgeKindTitles, sgeDimTitles, sgeCardOf, sgeCardW, sgeCardText,
    sgeTextW } from '@/utils/schemeGraphErd'

export const SG2_NODE_VIEW_FLAG = 'fe.ux.schemeGraphNodeView'
export function sg2NodeViewOn(setting) {
    try { return (typeof setting === 'function' ? setting(SG2_NODE_VIEW_FLAG, true) : true) !== false } catch (e) { return true }
}
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v)
const str = (v) => (v == null ? '' : String(v))
// сделанные виды (порядок умолчания) и резерв
export const SG2_NODE_VIEW_IMPL = Object.freeze(['box', 'card'])
export const SG2_NODE_VIEW_RESERVED = Object.freeze(['cascade'])   // резерв: каскад — место в списке и в коде, рисуется как box, пока не сделан
export const SG2_NODE_VIEW_DEFAULT = 'box'
// подписи умолчания (английская база, перевод — uk.json / ru.json)
export const SG2_NODE_VIEW_TITLES = Object.freeze({ box: 'Boxes', card: 'Cards', cascade: 'Cascade' })
const pick = (attr, k) => {
    if (!isObj(attr)) return undefined
    if (attr[k] !== undefined && attr[k] !== null) return attr[k]
    return isObj(attr.params) ? attr.params[k] : undefined
}
const idOf = (v) => str(isObj(v) ? (v.id ?? v.name ?? v.value) : v).trim().toLowerCase()
// список видов: params.node_views (объекты { id, title, title_uk, title_ru } или строки id) — только сделанные, без повторов, в порядке бэка; title — на языке
// пользователя (title_<lang>, иначе title, иначе подпись умолчания через t()); пусто / не массив / ни одного сделанного — box, card
export function sg2NodeViews(attr, lang, tt = t) {
    const tr = typeof tt === 'function' ? tt : (s) => s
    const def = (id) => tr(SG2_NODE_VIEW_TITLES[id] || id)
    const raw = pick(attr, 'node_views')
    const out = []
    if (Array.isArray(raw)) {
        for (const v of raw) {
            const id = idOf(v)
            if (!SG2_NODE_VIEW_IMPL.includes(id) || out.some((x) => x.id === id)) continue
            const title = isObj(v) ? localized(v, 'title', lang) : ''
            out.push({ id, title: title || def(id) })
        }
    }
    if (!out.length) for (const id of SG2_NODE_VIEW_IMPL) out.push({ id, title: def(id) })
    return out
}
// умолчание вида: params.node_view бэка, если он есть в списке; иначе box (если в списке), иначе первый
export function sg2NodeViewDefault(attr, views) {
    const list = Array.isArray(views) ? views : []
    const has = (id) => list.some((x) => x.id === id)
    const b = idOf(pick(attr, 'node_view'))
    if (b && has(b)) return b
    return has(SG2_NODE_VIEW_DEFAULT) ? SG2_NODE_VIEW_DEFAULT : (list[0] ? list[0].id : SG2_NODE_VIEW_DEFAULT)
}
// вид на экране: выключатель выкл — box; настройка вида (saved) из списка — она; иначе умолчание
export function sg2NodeViewPick(saved, attr, views, on = true) {
    if (!on) return SG2_NODE_VIEW_DEFAULT
    const s = idOf(saved), list = Array.isArray(views) ? views : []
    if (s && list.some((x) => x.id === s)) return s
    return sg2NodeViewDefault(attr, list)
}
// пункт быстрой панели ⚙ (GraphQuickPanel: список — круги, клик — set): один вид в списке — пункта нет
export function sg2NodeViewOption(views, value, tt = t) {
    const list = Array.isArray(views) ? views : []
    if (list.length < 2) return null
    const tr = typeof tt === 'function' ? tt : (s) => s
    return { key: 'node_view', title: tr('Node view'), value, viewParam: true, items: list.map((x) => ({ id: x.id, title: x.title })) }
}

// ---------- вид 'card' ----------
// привязки карточки — те же, что у ERD (sgeConfig: node_fields_* / node_horizons_* / fields_* / horizons_binding / nodes_kind_binding); таблиц записи атрибут графа может не задавать —
// умолчания по записи DATA-SCHEME (как у вкладки ERD бэка, scheme_erd()). Заданные атрибутом (верхний уровень или params) — главнее
export const GRAPH_CARD_BINDINGS = Object.freeze({ node_fields_binding: 'doc.node_fields', fields_binding: 'doc.fields', node_horizons_binding: 'doc.node_horizons', horizons_binding: 'doc.horizons' })
export function sg2CardConfig(attr) {
    const a = isObj(attr) ? attr : {}
    const p = isObj(a.params) ? a.params : {}
    return sgeConfig({ ...a, params: { ...GRAPH_CARD_BINDINGS, ...p } }).cfg
}
// карточки листьев графа g (sg2Graph): data { nodesRows, nfRows, fieldsRows, nhRows, horizonsRows }; o { lang, measure(s, font), colorsOn }.
// Цвет — как у ERD: свой цвет узла, иначе цвет группы верхнего уровня, иначе цвет смуги по её номеру (узлы вне групп — после групп); выкл цветов — нейтральный.
// Ширина — по содержимому (cardMinW…cardMaxW); колонки групп (g.mode 'lanes') — общая ширина колонки, как смуга ERD. → Map id листа → карточка (w, h, подписи; без места)
export function sg2Cards(g, data, cfg, o = {}) {
    const out = new Map()
    if (!g || !Array.isArray(g.leaves) || !g.nodes || !Array.isArray(g.nodes.leaves)) return out
    const lang = o.lang || ''
    const m = typeof o.measure === 'function' ? o.measure : sgeTextW
    const colorsOn = o.colorsOn !== false
    const d = data || {}
    const fields = sgeFieldsMap(d.fieldsRows, cfg, lang)
    const horizons = sgeHorizonsMap(d.horizonsRows, cfg, lang)
    const nf = sgeNodeFields(d.nfRows, cfg, lang, fields)
    const nh = sgeNodeHorizons(d.nhRows, cfg, horizons)
    const rowsAt = sgeRowsAt(d.nodesRows, cfg)
    const tops = (g.groups || []).filter((gr) => gr.depth === 0)
    const kindT = sgeKindTitles(), dimT = sgeDimTitles()
    g.nodes.leaves.forEach((leaf, i) => {
        const v = g.leaves[i]
        if (!v || !leaf) return
        const ti = tops.findIndex((gr) => gr.gid === leaf.groups[0])
        const li = ti >= 0 ? ti : tops.length
        const gr = ti >= 0 ? tops[ti] : null
        const laneColor = colorsOn ? ((gr && sg2Hex(gr.color)) || SGE_LANE_COLORS[li % SGE_LANE_COLORS.length]) : SGE_PLAIN
        const c = sgeCardOf(leaf, v, rowsAt.get(leaf.path), { cfg, nf, nh, colorsOn, laneColor, lane: li, gid: gr ? gr.gid : '', kindT, dimT })
        c.id = v.id
        c.w = Math.ceil(Math.min(SGE.cardMaxW, sgeCardW(c, m)))
        out.set(v.id, c)
    })
    if (g.mode === 'lanes' && Array.isArray(g.lanes)) {
        for (const ln of g.lanes) {
            const cs = (ln.leaves || []).map((id) => out.get(id)).filter(Boolean)
            const w = Math.max(0, ...cs.map((c) => c.w))
            for (const c of cs) c.w = w
        }
    }
    for (const c of out.values()) sgeCardText(c, m)
    return out
}
// граф с размерами узлов по карточкам (авторасстановка, рамки групп, маршруты связей, попадание — по ним); листья без карточки — как были. Новый объект (подпись
// раскладки sg2LayoutSig меняется — раскладка пересчитывается), g не меняется
export function sg2CardGraph(g, cards) {
    if (!g || !(cards instanceof Map) || !cards.size) return g
    return { ...g, leaves: g.leaves.map((l) => { const c = cards.get(l.id); return c ? { ...l, w: c.w, h: c.h } : l }), nodeView: 'card' }
}
